import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PackagingApiAdapter } from '../adapters/PackagingApiAdapter';
import { CreatePackagingRequest } from '../models/Packaging';
import { PackagingService } from '../services/PackagingService';

interface UseCreatePackagingOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useCreatePackaging = (options: UseCreatePackagingOptions = {}) => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PackagingApiAdapter(httpClient);
  const packagingService = new PackagingService(apiAdapter);
  const { toast } = useToast();
  const { t } = useTranslation('packagingModule');

  const mutation = useMutation({
    mutationFn: (data: CreatePackagingRequest) => packagingService.createPackaging(data),
    onSuccess: () => {
      // Invalidar queries de packaging para refrescar el listado
      queryClient.invalidateQueries({ queryKey: ['packaging'] });

      toast({
        title: t('messages.create_success'),
        description: t('modal.create.success_description'),
        variant: 'default',
      });

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.log('🔍 useCreatePackaging - Error:', error);

      toast({
        title: t('messages.create_error'),
        description: error.message || t('errors.unexpected'),
        variant: 'destructive',
      });

      options.onError?.(error);
    },
  });

  return {
    createPackaging: mutation.mutate,
    createPackagingAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};
